import React, {
  ReactElement,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useTranslation } from "react-i18next";
import { getIsMobile, useWindowSize } from "@songtradr/spa-common";
import * as uuid from "uuid";
import {
  IFeeSplitDetailState,
  ITrackOwnershipLabelConfig,
  TalentType,
} from "src/pages/projects/project/interfaces";
import { Currencies, Operation } from "src/constants";
import { IMonetaryValue } from "src/interfaces/monetary-value";
import CurrencyAndPrice from "src/components/currency-and-price";
import { FormInstance } from "antd/lib/form";
import {
  IAgencyWithContactsResults,
  IContactsResponse,
} from "src/api/talent-hub/interfaces";
import { IFeeSplitConfig } from "../../../music/components/final-track-ownership/interfaces";
import mainStyles from "../../../../styles";
import FeeSplitRowWithTalent from "../../../music/components/fee-split-row-with-talent";

export enum FeeSplitName {
  writerFee = "writerFee",
  masterFee = "masterFee",
  publisherFee = "publisherFee",
}
interface IProps {
  // We should use useFormInstance instead of passing down the form as prop, but current antd version does not support that
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  form: FormInstance<any>;
  totalMonetaryValue?: IMonetaryValue;
  items: Array<IFeeSplitDetailState>;
  labelConfig: ITrackOwnershipLabelConfig;
  isWritersShareSection?: boolean;
  propertyName: string;
  talentsResponse?: IContactsResponse;
  validateFeeField: boolean;
  validateDetailsFields: boolean;
  areTalentsLoading: boolean;
  agenciesAndContacts: IAgencyWithContactsResults[];
  onScrollTalents: (
    page: number,
    searchText: string,
    areAgenciesIntegrated: boolean,
    isServiceProvider: boolean
  ) => void;
  onResetNarrowSearch: () => void;
  onUpdateNarrowSearchText: (
    searchValue: string,
    areAgenciesIntegrated: boolean,
    isServiceProvider: boolean
  ) => void;
  onUpdateFee: (field: string, updatedFee: IMonetaryValue) => void;
  onPerformOperation: (
    operation: Operation,
    data: { index: number; value?: IFeeSplitDetailState }
  ) => void;
}

const FeeSplitWithTalent = ({
  totalMonetaryValue,
  items,
  form,
  labelConfig,
  agenciesAndContacts,
  isWritersShareSection = false,
  talentsResponse,
  propertyName,
  validateFeeField = true,
  validateDetailsFields = true,
  areTalentsLoading,
  onScrollTalents,
  onResetNarrowSearch,
  onUpdateNarrowSearchText,
  onPerformOperation,
  onUpdateFee,
}: IProps): ReactElement => {
  const { t } = useTranslation();
  useWindowSize();
  const isMobile = getIsMobile();
  const [rowItems, setRowItems] = useState<IFeeSplitDetailState[]>([]);

  const [totalFee, setTotalFee] = useState<number>(
    totalMonetaryValue?.value ?? 0
  );

  const handleFeeChange = useCallback(
    (field: string, updatedFee: IMonetaryValue) => {
      setTotalFee(updatedFee.value);
      onUpdateFee(field, updatedFee);
    },
    [onUpdateFee]
  );

  useEffect(() => {
    if (items.length !== 0) {
      setRowItems(items);
    } else {
      setRowItems([
        {
          contactReferences: [
            {
              name: "",
              email: "",
              agencyWritingPartnership: "",
              groupByAgency: false,
              type: TalentType.OpenForm,
            },
          ],
          split: "100",
          isNew: true,
          key: uuid.v4(),
        },
      ]);
    }
  }, [items]);

  const handleAddNewRowItem = useCallback(() => {
    onPerformOperation(Operation.add, { index: items.length });
  }, [items.length, onPerformOperation]);

  const handleDeleteRowItem = useCallback(
    (index: number) => {
      onPerformOperation(Operation.remove, { index });
    },
    [onPerformOperation]
  );

  const handleUpdateRowItem = useCallback(
    (index: number, feeSplitDetailState: IFeeSplitDetailState) => {
      onPerformOperation(Operation.update, {
        index,
        value: feeSplitDetailState,
      });
    },
    [onPerformOperation]
  );

  const totalMonetaryValueCurrency = useMemo(() => {
    return totalMonetaryValue?.currency ?? Currencies.USD.code;
  }, [totalMonetaryValue?.currency]);

  const totalMonetaryValuePrice = useMemo(() => {
    return totalMonetaryValue?.value ?? "";
  }, [totalMonetaryValue?.value]);

  return (
    <>
      <div css={mainStyles.collapsibleSectionContainer}>
        <div css={!isMobile && mainStyles.sizeOfQuarterDiv}>
          <CurrencyAndPrice
            currency={totalMonetaryValueCurrency}
            price={totalMonetaryValuePrice}
            currencyFormItemName={`${propertyName}Currency`}
            priceFormItemName={`${propertyName}Price`}
            testId="fee-currency"
            onChange={handleFeeChange}
            label={t(labelConfig.totalFeeLabel)}
            propertyName={propertyName}
            validateRequiredFields={validateFeeField}
          />
        </div>
        {(rowItems ?? []).map((feeSplit, feeSplitIndex) => {
          const isLastItem = feeSplitIndex === rowItems.length - 1;
          return (
            <FeeSplitRowWithTalent
              propertyName={`${propertyName}Splits`}
              validateRequiredFields={validateDetailsFields}
              // eslint-disable-next-line react/no-array-index-key
              key={`${propertyName}Splits-${feeSplitIndex}`}
              form={form}
              feeSplitDetail={feeSplit}
              config={labelConfig as IFeeSplitConfig}
              index={feeSplitIndex}
              isLastItem={isLastItem}
              agenciesAndContacts={agenciesAndContacts}
              areTalentsLoading={areTalentsLoading}
              isWritersShareSection={isWritersShareSection}
              talentsResponse={talentsResponse}
              totalFee={totalFee.toString()}
              onDeleteRow={handleDeleteRowItem}
              onUpdateRow={handleUpdateRowItem}
              onResetNarrowSearch={onResetNarrowSearch}
              onScrollTalents={onScrollTalents}
              onUpdateNarrowSearchText={onUpdateNarrowSearchText}
              onAddNewRowItem={handleAddNewRowItem}
            />
          );
        })}
      </div>
    </>
  );
};

export default FeeSplitWithTalent;
